import React, { useCallback, useState, useEffect } from 'react';
import { translate } from '../../Services/translation';
import Icon from '../Icon';
import Cart from './Checkout.Cart';
import ConfirmOrder from './Checkout.ConfirmOrder';
import DeliveryMethods from './Checkout.DeliveryMethods';
import PaymentMethods from './Checkout.PaymentMethods';
import PrivateCustomerInfo from './Checkout.PrivateCustomerInfo';
import BusinessCustomerInfo from './Checkout.BusinessCustomerInfo';
import EmptyCart from './Checkout.EmptyCart';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import PaymentWidget from '../Payments/PaymentWidget';
import {
  PaymentIntegrationType,
  ShippingIntegrationType,
} from '../../constants';
import clsx from 'clsx';
import CheckoutOrderSummary from './Checkout.OrderSummary';
import CheckoutSection from './utils/Checkout.Section';
import OrderReference from './Checkout.OrderReference';

const Checkout = () => {
  const { payload } = useTypedSelector((state) => state.checkout);
  const cart = useTypedSelector<any>((state) => state.cart);
  const [orderRows] = useState(cart?.orderRows);
  const hasOrderRows = orderRows && Object.keys(orderRows).length > 0;
  const numProducts = hasOrderRows
    ? Object.values(cart.orderRows).length
    : 0;
  const {
    message,
    isBusinessCustomer,
    paymentWidget,
    deliveryMethods,
    selectedDeliveryMethod,
    selectedPaymentMethod,
    paymentMethods,
    errorMessages,
  } = payload;

  const [paymentWidgetVisibility, setPaymentWidgetVisibility] =
    useState(true);
  const [paymentOptionVisibility, setPaymentOptionVisibility] =
    useState(true);
  const [customerDetailsVisibility, setCustomerDetailsVisibility] =
    useState(true);
  const [deliveryWidgetVisibility, setDeliveryWidgetVisibility] =
    useState(true);
  const [confirmOrderVisibility, setConfirmOrderVisibility] =
    useState(true);
  const [signUpCheckboxVisibility, setSignUpCheckboxVisibility] =
    useState(true);
  const [shippingOptionVisibility, setShippingOptionVisibility] =
    useState(true);

  const checkIntegrationTypeExist = useCallback(
    (integrationTypeCheck) => {
      return deliveryMethods.find(
        (method) => method.integrationType === integrationTypeCheck
      );
    },
    [DeliveryMethods]
  );

  const resetVisibilityValues = useCallback(() => {
    setShippingOptionVisibility(true);
    setPaymentOptionVisibility(true);
    setCustomerDetailsVisibility(true);
    setDeliveryWidgetVisibility(true);
    setPaymentWidgetVisibility(true);
    setConfirmOrderVisibility(true);
    setSignUpCheckboxVisibility(true);
  }, []);

  // Show or hide different sections depending on payment and delivery methods.
  useEffect(() => {
    resetVisibilityValues();
    switch (selectedPaymentMethod?.integrationType) {
      case PaymentIntegrationType.IframeCheckout:
        setCustomerDetailsVisibility(false);
        setConfirmOrderVisibility(false);
        break;
      case PaymentIntegrationType.PaymentWidgets:
        setConfirmOrderVisibility(false);
        setSignUpCheckboxVisibility(false);
        break;
      case PaymentIntegrationType.DirectPayment:
        setPaymentWidgetVisibility(false);
        break;
    }
    if (
      checkIntegrationTypeExist(ShippingIntegrationType.DeliveryCheckout)
    ) {
      return setShippingOptionVisibility(false);
    }
    if (
      checkIntegrationTypeExist(ShippingIntegrationType.PaymentCheckout)
    ) {
      setShippingOptionVisibility(false);
      setDeliveryWidgetVisibility(false);
      return;
    }
    if (
      selectedDeliveryMethod?.integrationType ===
      ShippingIntegrationType.Inline
    ) {
      setDeliveryWidgetVisibility(false);
    }
  }, [
    selectedDeliveryMethod,
    selectedPaymentMethod,
    deliveryMethods,
    paymentMethods,
    resetVisibilityValues,
    checkIntegrationTypeExist,
  ]);

  const responseString = paymentWidget
    ? paymentWidget.responseString
    : null;
  const updateKey = paymentWidget ? paymentWidget._force_update : null;

  if (numProducts <= 0) {
    return (
      <div
        className={clsx(
          'py-4',
          'px-0',
          'bg-gray-50',
          'lg:py-8',
          'min-h-[calc(100vh-400px)]',
          'flex',
          'flex-col'
        )}
      >
        <EmptyCart />
      </div>
    );
  }
  return (
    <div className={clsx('py-4', 'px-0', 'bg-gray-50', 'lg:py-8')}>
      <div className={clsx('lg:container', 'px-0')}>
        <h1 className="md-down:container mb-2 uppercase lg:mb-4">
          {translate('checkout.title')}
        </h1>
        {message && (
          <div
            className={clsx(
              'flex',
              'flex-row',
              'items-center',
              'p-3',
              'bg-white',
              'border',
              'border-solid',
              'border-gray-900',
              'rounded',
              'mx-4',
              'mb-4',
              'text-lg',
              'lg:m-0',
              'lg:mb-6'
            )}
          >
            <Icon className={clsx('w-6', 'h-6', 'mr-2')} name="info" />
            <span>{message}</span>
          </div>
        )}
        {errorMessages['general'] && (
          <div className="mx-4 mb-4 flex flex-row gap-2 rounded border border-solid border-error-500 bg-white p-3 text-lg lg:m-0 lg:mb-6">
            <Icon className="h-6 w-6 text-error-500" name="warning" />
            {errorMessages['general'].map((error) => (
              <span className="flex text-lg text-error-500" key={error}>
                {error}
              </span>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-6 xl:gap-12">
          <div className="flex flex-col gap-4 lg:gap-6">
            <Cart />
            {!isBusinessCustomer && customerDetailsVisibility && (
              <PrivateCustomerInfo showSignUp={signUpCheckboxVisibility} />
            )}
            {isBusinessCustomer && customerDetailsVisibility && (
              <BusinessCustomerInfo />
            )}
            {shippingOptionVisibility && <DeliveryMethods />}
            {paymentOptionVisibility && <PaymentMethods />}
            {!paymentWidget && !paymentWidgetVisibility && (
              <OrderReference />
            )}
            {confirmOrderVisibility && <ConfirmOrder />}
            {paymentWidget && paymentWidgetVisibility && (
              <PaymentWidget
                key={updateKey}
                responseString={responseString}
              />
            )}
          </div>
          <CheckoutSection className="md-down:hidden h-fit">
            <CheckoutOrderSummary />
          </CheckoutSection>
        </div>
      </div>
    </div>
  );
};
export default Checkout;
